/** 人生文本只解释实际可见病例和成功收据；旧文字不反解析为诊断或账单。 */
type Data = Record<string, any>;
const list = (x: unknown): Data[] => Array.isArray(x) ? x : [];
const known = (x: unknown): string => x == null || x === '' ? '未知' : String(x);
const day = (x: unknown): string => typeof x === 'number' && Number.isInteger(x) && x >= 0
  ? `第 ${Math.floor(x / 360) + 1} 年 ${Math.floor(x % 360 / 30) + 1} 月 ${x % 30 + 1} 日` : '日期未知';
const amount = (x: unknown, unit = 1): string => typeof x === 'number' && Number.isFinite(x) ? `${(x / unit).toFixed(2)} 元` : '金额未知';
const hospital = (x: unknown): string => typeof x === 'number' ? `医院 #${x}` : x && typeof x === 'object'
  ? `${known((x as Data).name)}${(x as Data).id != null ? `（医院 #${(x as Data).id}）` : ''}` : '医院未知';
const condition = (e: Data): string => e.diagnosis?.name ? String(e.diagnosis.name) : '未确诊，病名未知';
const outcome = (e: Data): string => e.outcome === 'death' ? '本人死亡时结束记录，非死因判定'
  : e.outcome === 'emigrated' ? '本人迁出时结束记录，非康复判定'
  : e.outcomeName || (e.outcome === 'recovered' ? '本次病程已结束' : e.statusName || '状态未知');

function episodeText(e: Data, closed: boolean): string[] {
  const lines = [`病程 #${known(e.id)} · ${condition(e)} · ${outcome(e)}`,
    `出现 ${day(e.since)}${closed ? ` · 结束 ${day(e.ended)}` : ''} · 当时症状 ${known(e.symptoms)}`];
  if (e.diagnosis) lines.push(`实际检查确诊：${known(e.diagnosis.name)} · ${day(e.diagnosis.confirmed)} · ${hospital(e.diagnosis.hospital ?? e.diagnosis.facility)}`);
  else lines.push('没有实际检查确诊记录，不从症状补猜病名。');
  for (const s of list(e.lastServices)) lines.push(`最近一次已完成 ${known(s.service)}：${day(s.day)}（不是服务总次数）`);
  return lines;
}

/** 付款是否完成以成功收据为准；pendingOrders 永远不混入实付。 */
export function medicalText(m: Data | null | undefined): string[] {
  const lines = ['疾病、住院与医保（本人资料）'];
  if (!m) return [...lines, '此版本未提供结构化医疗状态；已归档旧记录仍按原样保留，不补猜病史。'];
  if (m.enabled === false) return [...lines, '此城市未启用当前临床病程规则；不能据此判断从未生病。'];
  const current = list(m.episodes), closed = list(m.closedEpisodes), orders = list(m.pendingOrders), bills = list(m.todayBills);
  lines.push(`医疗资料截至 ${day(m.day)} · 本城首条实际临床记录 ${day(m.historySince)}`,
    `当前显现病程 ${current.length} 段；未显现病种不在此列。`, ...current.flatMap(e => episodeText(e, false)));
  if (!current.length) lines.push('当前没有已显现病程，不代表从未生病。');
  lines.push(`近期既往摘要 ${closed.length} / 本城实际可见结束病程 ${known(m.closedObservedCount)} 段；最多保留最近 ${known(m.closedLimit)} 段，不是终身全病史。`,
    ...closed.flatMap(e => episodeText(e, true)));
  if (!closed.length) lines.push(m.closedEpisodes == null ? '此版本未保留结构化既往摘要。' : '没有近期已结束病程摘要。');
  lines.push(`近期诊疗记录 ${known(m.retainedRecordCount)} / 实际临床记录 ${known(m.recordCount)} 条；近期窗口 ${known(m.historyLimit)} 条。更早已归档记录见下方事件，未保存的旧史不可恢复。`);
  if (m.injuryRecoveryUntil != null) lines.push(`工伤恢复期限：${day(m.injuryRecoveryUntil)}；一次病程结束不等于解除全部限制。`);
  if (m.admission) lines.push(`当前住院：病程 #${known(m.admission.episode)} · ${hospital(m.admission.hospital)} · 入院 ${day(m.admission.since)} · 最近实际护理 ${day(m.admission.lastCare)}${m.admission.waitingCoverage ? ' · 等待有效付款或保障，仍占床' : ''}`);
  else lines.push('当前没有住院登记。');
  lines.push(`待完成医嘱 ${orders.length} 条（不是已完成服务或已付费用）`);
  for (const o of orders) lines.push(`病程 #${known(o.episode)} · 医嘱 #${known(o.order)} · 计划 ${known(o.service)} · ${day(o.day)} · ${hospital(o.hospital)} · 数量 ${known(o.units)}`);
  if (m.refusal) lines.push(`本人最近未完成原因：${m.refusal}（不推定属于每张待办医嘱）`);
  lines.push(`${day(m.day)} 当日成功账单 ${bills.length} 笔（此处金额元，不是全病程费用）`);
  for (const b of bills) lines.push(`病程 #${known(b.episode)} · 医嘱 #${known(b.order)} · ${known(b.service)} · ${hospital(b.hospital)}：家庭自付 ${amount(b.patient)} · 雇主代付 ${amount(b.employer)} · 基本医保 ${amount(b.public)} · 补充或工伤保险 ${amount(b.insurer)} · 医院实收 ${amount(b.total)}`);
  if (m.cover) {
    const c = m.cover;
    lines.push('本人医保累计（不同于单次病程费用，额度不等于已经支付）',
      `基本医保剩余额度 ${amount(c.basicLeft)} · 约定有效至 ${c.basicUntil > 0 ? day(c.basicUntil) : '未登记有效期限'}`,
      `基本医保累计缴费 ${amount(c.contributions)} · 累计实付 ${amount(c.publicPaid)}`,
      `补充医保剩余额度 ${amount(c.extraLeft)} · 约定有效至 ${c.extraUntil > 0 ? day(c.extraUntil) : '未登记有效期限'}`,
      `补充医保机构 ${c.insurer ? `${known(c.insurer.name)}（公司 #${known(c.insurer.id)}）` : '未登记'}`,
      `补充医保累计保费 ${amount(c.premiums)} · 累计理赔 ${amount(c.claims)}`);
  }
  return lines;
}

/** 每条冷档只用该条当时的快照；不能拿今天的诊断填回以前。 */
export function medicalRecordText(d: Data): string[] {
  const s = d.snapshot, b = d.receipt;
  const lines = [`医疗事件 ${known(d.event)} · 病程 #${known(d.episode)}`];
  if (d.imported) lines.push('此条由旧热记录导入；未保存的当时快照和收据不补造。');
  if (d.snapshotAsOf != null) lines.push(`病例快照实际观察于 ${day(d.snapshotAsOf)}`);
  if (s) lines.push(...episodeText(s, s.ended != null));
  else lines.push('未保留当时病例快照，病名、状态与死因不能由这条文字反推。');
  if (typeof d.detail === 'string') lines.push(d.detail);
  if (!b) return [...lines, '本条未包含成功收费收据，不能反推费用。'];
  lines.push(`本次成功收据：${day(b.day)} · ${known(b.service)} · 病程 #${known(b.episode)} · 医嘱 #${known(b.order)} · ${hospital(b.hospital)} · 实际收款公司 #${known(b.provider)}`);
  if (b.moneyUnit !== 'cent') return [...lines, '收据原始金额单位未确认，不换算猜测；请查看原始完整资料附录。'];
  lines.push(`家庭自付 ${amount(b.patient, 100)} · 雇主代付 ${amount(b.employer, 100)} · 基本医保 ${amount(b.public, 100)} · 补充或工伤保险 ${amount(b.insurer, 100)} · 医院实收 ${amount(b.total, 100)}（原始金额分）`);
  const values = [b.patient, b.employer, b.public, b.insurer, b.total];
  if (values.every(x => typeof x === 'number' && Number.isSafeInteger(x))) {
    const difference = b.patient + b.employer + b.public + b.insurer - b.total;
    lines.push(difference === 0 ? '四方实付与本张收据医院实收一致。' : `本张收据付款合计与医院实收不一致，差额 ${amount(difference, 100)}；保留原值待核对。`);
  } else lines.push('本张收据付款字段不完整，无法核对。');
  return lines;
}
